import React, { useState, useEffect, useContext } from 'react';
import {
    Plus, Receipt, Wallet, FileText, Trash2, Edit3, X, Camera, ArrowLeft,
    CheckCircle, UserPlus, History, Landmark, Image as ImageIcon, MessageSquare
} from 'lucide-react';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

const VendorLedger = () => {
    const { user } = useContext(AuthContext);
    const [view, setView] = useState('list');
    const [vendors, setVendors] = useState([]);
    const [projects, setProjects] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modals & Drawers
    const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
    const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);

    // Tracking States
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [payments, setPayments] = useState([]);

    // Form States
    const [vendorForm, setVendorForm] = useState({ name: '', contact_person: '', phone: '', gstin: '', address: '' });
    const [formData, setFormData] = useState({
        id: null, vendor_id: '', invoice_no: '', invoice_date: new Date().toISOString().split('T')[0],
        taxable_amount: '', gst_rate: 18, project_id: '', bill_copy: null
    });
    const [paymentForm, setPaymentForm] = useState({
        amount: '', payment_date: new Date().toISOString().split('T')[0],
        notes: '', attachment: null
    });

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const [vRes, pRes, iRes] = await Promise.all([
                api.get('get_vendors.php'),
                api.get('get_projects.php'),
                api.get('get_all_vendor_invoices.php')
            ]);
            setVendors(vRes.data.vendors || []);
            setProjects(pRes.data.projects || []);
            setInvoices(iRes.data.data || []);
        } catch (err) { console.error("Error loading ledger data", err); }
    };

    // --- Invoice Actions ---
    const handleSaveInvoice = async (e) => {
        e.preventDefault();
        setLoading(true);
        const data = new FormData();
        Object.keys(formData).forEach(key => {
            if (formData[key] !== null) data.append(key, formData[key]);
        });

        const endpoint = formData.id ? 'update_vendor_invoice.php' : 'save_vendor_invoice.php';
        const res = await api.post(endpoint, data);

        if (res.data.status === 'success') {
            setView('list');
            loadInitialData();
            resetInvoiceForm();
        }
        setLoading(false);
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation(); // Prevent opening drawer
        if (window.confirm("Delete this invoice and all associated payments?")) {
            await api.post('delete_vendor_invoice.php', { id });
            loadInitialData();
        }
    };

    const resetInvoiceForm = () => {
        setFormData({ id: null, vendor_id: '', invoice_no: '', invoice_date: new Date().toISOString().split('T')[0], taxable_amount: '', gst_rate: 18, project_id: '', bill_copy: null });
    };

    // --- Payment Actions ---
    const openPaymentDetails = async (invoice) => {
        setSelectedInvoice(invoice);
        const res = await api.get(`get_vendor_payments.php?invoice_id=${invoice.id}`);
        setPayments(res.data.payments || []);
        setIsPaymentDrawerOpen(true);
    };

    const handleRecordPayment = async (e) => {
        e.preventDefault();

        // Safety check: ensure selectedInvoice exists
        if (!selectedInvoice || !selectedInvoice.id) {
            alert("No invoice selected.");
            return;
        }

        const data = new FormData();
        // Use the values directly from your state
        data.append('invoice_id', selectedInvoice.id);
        data.append('amount', paymentForm.amount);
        data.append('payment_date', paymentForm.payment_date);
        data.append('notes', paymentForm.notes || '');

        // Check if attachment exists before appending
        if (paymentForm.attachment) {
            data.append('attachment', paymentForm.attachment);
        }

        try {
            setLoading(true);
            const res = await api.post('record_vendor_payment.php', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.status === 'success') {
                // Reset the small payment form
                setPaymentForm({
                    amount: '',
                    payment_date: new Date().toISOString().split('T')[0],
                    notes: '',
                    attachment: null
                });

                // Refresh the specific payment timeline in the drawer
                const updatedPay = await api.get(`get_vendor_payments.php?invoice_id=${selectedInvoice.id}`);
                setPayments(updatedPay.data.payments || []);

                // Refresh the main ledger list to update "Paid" and "Pending" columns
                loadInitialData();

                alert(res.data.message);
            } else {
                alert("Error: " + res.data.message);
            }
        } catch (err) {
            console.error("Payment failed", err);
            alert("Server error while recording payment.");
        } finally {
            setLoading(false);
        }
    };

    // --- Vendor Master Actions ---
    const handleAddVendor = async (e) => {
        e.preventDefault();
        const res = await api.post('save_vendor.php', vendorForm);
        if (res.data.status === 'success') {
            setIsVendorModalOpen(false);
            setVendorForm({ name: '', contact_person: '', phone: '', gstin: '', address: '' });
            loadInitialData();
        }
    };

    const taxable = parseFloat(formData.taxable_amount || 0);
    const totalGst = (taxable * formData.gst_rate) / 100;
    const grandTotal = taxable + totalGst;

    return (
        <div className="max-w-7xl mx-auto space-y-8 p-4">
            {/* HEADER */}
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-5xl font-black text-slate-900 tracking-tighter italic">Vendor Ledger</h1>
                    <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest mt-2">{`${user.company_name} • Procurement & Payables`}</p>
                </div>
                {view === 'list' && (
                    <button onClick={() => setView('form')} className="bg-indigo-600 text-white px-10 py-5 rounded-[24px] font-black shadow-2xl flex items-center gap-3 hover:scale-105 transition-transform">
                        <Plus size={20} /> New Purchase
                    </button>
                )}
            </div>

            {view === 'list' ? (
                /* INVOICE LIST TABLE */
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <tr>
                                <th className="p-8">Vendor & Date</th>
                                <th className="p-8 text-right">Taxable</th>
                                <th className="p-8 text-right">Total</th>
                                <th className="p-8 text-right text-rose-500">Pending</th>
                                <th className="p-8 text-center">Status</th>
                                <th className="p-8 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {invoices.map((inv) => (
                                <tr key={inv.id} onClick={() => openPaymentDetails(inv)} className="hover:bg-indigo-50/50 cursor-pointer transition-colors group">
                                    <td className="p-8">
                                        <p className="font-black text-slate-900 text-sm uppercase">{inv.vendor_name}</p>
                                        <p className="text-[10px] font-bold text-indigo-500 uppercase">{inv.invoice_date} • {inv.project_name || 'General'}</p>
                                    </td>
                                    <td className="p-8 text-right font-bold text-slate-600">₹{parseFloat(inv.taxable_amount).toLocaleString()}</td>
                                    <td className="p-8 text-right font-black text-slate-900">₹{parseFloat(inv.total_amount).toLocaleString()}</td>
                                    <td className="p-8 text-right">
                                        <span className="font-black text-rose-500 italic">₹{(inv.total_amount - inv.paid_amount).toLocaleString()}</span>
                                    </td>
                                    <td className="p-8 text-center">
                                        <span className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase ${inv.status === 'fully_paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {inv.status.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td className="p-8 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => { setFormData({ ...inv, bill_copy: null }); setView('form'); }} className="p-3 hover:bg-white rounded-xl text-slate-400 hover:text-indigo-600 border border-transparent hover:border-slate-100"><Edit3 size={18} /></button>
                                            <button onClick={(e) => handleDelete(e, inv.id)} className="p-3 hover:bg-white rounded-xl text-slate-400 hover:text-rose-600 border border-transparent hover:border-slate-100"><Trash2 size={18} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                /* ADD / EDIT FORM */
                <form onSubmit={handleSaveInvoice} className="bg-white rounded-[48px] p-12 border border-slate-100 shadow-2xl max-w-3xl mx-auto animate-in slide-in-from-bottom-10 duration-500">
                    <div className="flex justify-between items-center mb-10">
                        <button type="button" onClick={() => { setView('list'); resetInvoiceForm(); }} className="p-4 bg-slate-100 rounded-2xl"><ArrowLeft size={20} /></button>
                        <h2 className="text-2xl font-black italic uppercase tracking-tighter">Purchase Entry</h2>
                        <button type="button" onClick={() => setIsVendorModalOpen(true)} className="flex items-center gap-2 p-3 bg-indigo-50 text-indigo-600 rounded-2xl text-[10px] font-black uppercase"><UserPlus size={16} /> Add Vendor</button>
                    </div>

                    <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Vendor</label>
                                <select required className="w-full p-5 bg-slate-50 rounded-3xl font-bold" value={formData.vendor_id} onChange={e => setFormData({ ...formData, vendor_id: e.target.value })}>
                                    <option value="">Choose Supplier...</option>
                                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Invoice Date</label>
                                <input type="date" required className="w-full p-5 bg-slate-50 rounded-3xl font-bold" value={formData.invoice_date} onChange={e => setFormData({ ...formData, invoice_date: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                            <input required className="w-full p-5 bg-slate-50 rounded-3xl font-bold" value={formData.invoice_no} onChange={e => setFormData({ ...formData, invoice_no: e.target.value })} placeholder="Invoice #" />
                            <select required className="w-full p-5 bg-slate-50 rounded-3xl font-bold" value={formData.project_id} onChange={e => setFormData({ ...formData, project_id: e.target.value })}>
                                <option value="">Select Project Site...</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                            </select>
                        </div>

                        <div className="p-8 bg-indigo-50 rounded-[40px] space-y-6 border border-indigo-100">
                            <div className="grid grid-cols-2 gap-6">
                                <input type="number" required className="w-full p-5 bg-white rounded-3xl font-black text-xl text-indigo-600" value={formData.taxable_amount} onChange={e => setFormData({ ...formData, taxable_amount: e.target.value })} placeholder="Taxable Amount" />
                                <select className="w-full p-5 bg-white rounded-3xl font-black text-xl text-indigo-600" value={formData.gst_rate} onChange={e => setFormData({ ...formData, gst_rate: e.target.value })}>
                                    <option value="18">18%</option><option value="28">28%</option><option value="12">12%</option><option value="5">5%</option><option value="0">0%</option>
                                </select>
                            </div>
                            <div className="pt-4 border-t border-indigo-100 flex justify-between items-center text-indigo-600 italic">
                                <p className="text-[10px] font-black uppercase">Grand Total (Incl GST)</p><h3 className="text-3xl font-black">₹{grandTotal.toLocaleString()}</h3>
                            </div>
                        </div>

                        <div className="relative h-32 border-2 border-dashed border-slate-200 rounded-[32px] flex flex-col items-center justify-center bg-slate-50">
                            <Camera className="text-slate-300 mb-2" size={32} />
                            <p className="text-[10px] font-black text-slate-400 uppercase">{formData.bill_copy ? formData.bill_copy.name : 'Bill Copy / Receipt Photo'}</p>
                            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => setFormData({ ...formData, bill_copy: e.target.files[0] })} />
                        </div>

                        <button disabled={loading} className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl hover:bg-indigo-600 transition-all flex items-center justify-center gap-2">
                            {loading ? 'Processing...' : <><CheckCircle size={20} /> {formData.id ? 'Update Ledger' : 'Confirm Purchase'}</>}
                        </button>
                    </div>
                </form>
            )}

            {/* PAYMENT DRAWER (Sidebar style) */}
            {isPaymentDrawerOpen && (
                <div className="fixed inset-0 z-[600] bg-slate-900/90 backdrop-blur-sm flex justify-end">
                    <div className="w-full max-w-2xl bg-white h-full p-12 overflow-y-auto animate-in slide-in-from-right duration-300">
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-3xl font-black italic tracking-tighter uppercase">Payment History</h2>
                            <button onClick={() => setIsPaymentDrawerOpen(false)} className="p-3 bg-slate-100 rounded-full"><X /></button>
                        </div>

                        {selectedInvoice && (
                            <div className="bg-slate-900 rounded-[32px] p-8 text-white mb-10 border-b-8 border-indigo-500 shadow-xl">
                                <p className="text-indigo-400 font-black text-[10px] uppercase tracking-widest mb-1">Total Outstanding</p>
                                <h3 className="text-4xl font-black italic">₹{(selectedInvoice.total_amount - selectedInvoice.paid_amount).toLocaleString()}</h3>
                                <div className="mt-6 flex justify-between items-center border-t border-white/10 pt-4">
                                    <div className="text-[10px] font-bold text-slate-400 uppercase">
                                        <p>{selectedInvoice.vendor_name}</p>
                                        <p>Bill #{selectedInvoice.invoice_no}</p>
                                    </div>
                                    <p className="text-[10px] font-black text-indigo-400 uppercase">Total Bill: ₹{parseFloat(selectedInvoice.total_amount).toLocaleString()}</p>
                                </div>
                            </div>
                        )}

                        {/* RECORD NEW PAYMENT FORM */}
                        <form onSubmit={handleRecordPayment} className="bg-slate-50 p-8 rounded-[32px] border border-slate-100 mb-10 space-y-4 shadow-sm">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Record Installment</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <input required type="number" placeholder="Amount ₹" className="p-5 rounded-2xl bg-white font-bold outline-none ring-0 focus:ring-2 focus:ring-indigo-500 transition-all" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
                                <input required type="date" className="p-5 rounded-2xl bg-white font-bold outline-none" value={paymentForm.payment_date} onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
                            </div>
                            <textarea placeholder="Payment notes (e.g. Paid via GPay, Ref #...)" className="w-full p-5 rounded-2xl bg-white font-bold text-sm outline-none" value={paymentForm.notes} onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
                            <div className="flex items-center gap-4">
                                <div className="relative flex-1 bg-white p-5 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center gap-2 cursor-pointer">
                                    <Camera size={18} className="text-slate-400" />
                                    <span className="text-[10px] font-black text-slate-400 uppercase">{paymentForm.attachment ? 'Image Selected' : 'Add Receipt Photo'}</span>
                                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => setPaymentForm({ ...paymentForm, attachment: e.target.files[0] })} />
                                </div>
                                <button className="px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase text-xs hover:bg-indigo-700 shadow-lg">Submit</button>
                            </div>
                        </form>

                        {/* TRANSACTION TIMELINE */}
                        <div className="space-y-4">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">Timeline</h4>
                            {payments.length === 0 ? (
                                <div className="p-10 border border-dashed border-slate-100 rounded-[32px] text-center">
                                    <History size={32} className="mx-auto text-slate-200 mb-2" />
                                    <p className="text-slate-300 font-bold italic text-sm">No transactions yet.</p>
                                </div>
                            ) : (
                                payments.map((p, i) => (
                                    <div key={i} className="flex justify-between items-center p-6 bg-white border border-slate-100 rounded-[24px] hover:border-indigo-100 transition-all group">
                                        <div className="flex items-center gap-5">
                                            <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl"><Landmark size={20} /></div>
                                            <div>
                                                <p className="font-black text-slate-900 text-lg">₹{parseFloat(p.amount_paid).toLocaleString()}</p>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase">{p.payment_date}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            {p.notes && <div title={p.notes} className="p-2 text-slate-300 hover:text-indigo-400 transition-colors"><MessageSquare size={18} /></div>}
                                            {p.attachment_path && (
                                                <a href={`https://backendibos.laxmaninterior.in/${p.attachment_path}`} target="_blank" rel="noreferrer" className="p-2 text-indigo-400 hover:scale-110 transition-transform">
                                                    <ImageIcon size={18} />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* MASTER VENDOR MODAL */}
            {isVendorModalOpen && (
                <div className="fixed inset-0 z-[700] bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-6">
                    <form onSubmit={handleAddVendor} className="bg-white w-full max-w-lg rounded-[48px] p-12 space-y-4 shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-black italic tracking-tighter uppercase">Master Supplier</h2>
                            <button type="button" onClick={() => setIsVendorModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full"><X /></button>
                        </div>
                        <input required placeholder="Vendor Business Name" className="w-full p-5 bg-slate-50 rounded-3xl font-bold border-none outline-none ring-0 focus:ring-2 focus:ring-indigo-500" value={vendorForm.name} onChange={e => setVendorForm({ ...vendorForm, name: e.target.value })} />
                        <div className="grid grid-cols-2 gap-4">
                            <input placeholder="Contact Person" className="w-full p-5 bg-slate-50 rounded-3xl font-bold text-xs" value={vendorForm.contact_person} onChange={e => setVendorForm({ ...vendorForm, contact_person: e.target.value })} />
                            <input placeholder="Phone Number" className="w-full p-5 bg-slate-50 rounded-3xl font-bold text-xs" value={vendorForm.phone} onChange={e => setVendorForm({ ...vendorForm, phone: e.target.value })} />
                        </div>
                        <input placeholder="GSTIN Number" className="w-full p-5 bg-slate-50 rounded-3xl font-bold text-xs" value={vendorForm.gstin} onChange={e => setVendorForm({ ...vendorForm, gstin: e.target.value })} />
                        <textarea placeholder="Business Address" className="w-full p-5 bg-slate-50 rounded-3xl font-bold text-xs h-24" value={vendorForm.address} onChange={e => setVendorForm({ ...vendorForm, address: e.target.value })} />
                        <button className="w-full py-6 bg-indigo-600 text-white rounded-[32px] font-black text-sm uppercase tracking-widest shadow-xl shadow-indigo-200">Save to Master Directory</button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default VendorLedger;